import {
  BeneficiaryCreateRequest,
  CopCheckRequest,
  CopCheckResult,
  ExchangeAcceptResult,
  ExchangeQuoteRequest,
  ExchangeQuoteResult,
  InternationalBeneficiaryCreateRequest,
  InternationalPaymentPrecheckResult,
  InternationalPaymentRequest,
  InternationalPaymentResult,
  OnboardBusinessRequest,
  OnboardBusinessResult,
  OnboardIndividualRequest as OnboardIndividualWidgetRequest,
  OnboardIndividualResult,
  OnboardingVerificationStates,
  PaymentRequestCreateRequest,
  ProfileUpdateRequest,
  SavingsAccountCreateRequest,
  SavingsAccountCreateResult,
  AppAccount,
  AppBeneficiary,
  AppBeneficiaryRequirements,
  AppBulkPayment,
  AppBulkPaymentSummary,
  AppBulkPaymentTransfer,
  AppBusinessAddress,
  AppBusinessMember,
  AppCard,
  AppCardLimit,
  AppContactVerification,
  AppFieldRequirement,
  AppNominatedAccount,
  AppOnboardingStatus,
  AppPaymentRequest,
  AppPaymentScheme,
  AppProfile,
  AppSavingsAccount,
  AppSavingsAccountRequest,
  AppSavingsProduct,
  AppSavingsProductDocuments,
  AppSavingsWithdrawalAllowance,
  AppScheduledPayment,
  AppTransaction,
  AppTransactionDetail,
  AppTransactionParty,
  AppTransferReason,
  AppVerificationChannel,
  isVerificationLive,
  normaliseOnboardingCheckStatus,
  outstandingPrerequisites,
  AppStoredDocument,
} from '@finxone-platform/shared/app-sdk';
import {
  ACCOUNTTYPE,
  AccountInfoType,
  BeneficiaryRequirementsResponse,
  BulkPaymentResultList,
  BulkPaymentsSummary,
  CardInfoType,
  CardLimitsType,
  CheckPayeeMatchStatus,
  ExchangeFixedType,
  ExchangeQuotePayload,
  ExchangeQuoteResponse,
  ExchangeTransactionResponse,
  NominatedAccountInfoDetailType,
  PaymentRailOptions,
  ProfileStateModel,
  SavingsAccountInfoType,
  SavingsProductType,
  TransactionType,
  UserProfileData,
  FileDocumentResponse,
} from '@finxone-platform/shared/sys-config-types';
import { PaymentRequestListResponse } from '@finxone-platform/shared/utils';
import {
  AddBeneficiaryRequest,
  BeneficiaryDestinationIdentifier,
  BeneficiaryIdentifierAddress,
  BulkPaymentHistoryResult,
  CancellablePaymentResponse,
  CheckPayeeResponse,
  GetBeneficiaryResponse,
  Payee,
  PaymentRequestorRequest,
  TransactionDetailResponse,
  TransactionInfoType,
  TransactionStateType,
  TransferMoneyInternationalResponse,
  TransferMoneyRequest,
  TransferReason,
} from '../../../../lib/services/account-service/account.type';
import {
  BusinessOnboardingAddressRequest,
  BusinessOnboardingMemberRequest,
  BusinessOnboardingRequest,
  OnboardIndividualRequest as OnboardIndividualWireRequest,
} from '../../../../lib/services/metadata-service/metadata-service.type';
import {
  AccountOpenRequestType,
  CreateSavingsAccountRequest,
  SavingsAccountInfoTypeResponse,
} from '../../../../lib/services/savings-account-service/savings-account.type';
import { PaymentRequestSummary } from '../../../../lib/state/notifications.state';

/**
 * Pure mappers from the Angular app's internal models onto the fx-widget/1
 * payload contracts (app-sdk contracts.ts). Everything a custom widget
 * sees crosses through here, so internal state-model churn never leaks into
 * the wire contract.
 */

/**
 * The wire payload of transaction listings carries id/transactionNumber
 * (see account-service TransactionInfoType) even though the frontend type
 * omits them; the host UI relies on the same fields
 * (recent-transaction-history-widget uses `id || transactionNumber`).
 */
export type TransactionWireItem = TransactionInfoType & { id?: string; transactionNumber?: string };

function toFiniteNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Parses provider string amounts, keeping absent values absent (never 0). */
function toOptionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function toWidgetAccount(account: AccountInfoType): AppAccount {
  const currency =
    account.accountType === 'multi-currency' ? (account.currency[0]?.currency ?? '') : account.currency;
  const availableBalance =
    account.accountType === 'multi-currency'
      ? account.availableBalance.find((balance) => balance.currency === currency)?.amount
      : account.availableBalance;

  const balanceFields = [
    'balance',
    'availableBalance',
    'indicativeBalance',
    'ledgerBalance',
    'ledgerBalanceIncludingPendingExchange',
  ] as const;
  const currencies =
    account.accountType === 'multi-currency'
      ? [
          ...new Set([
            ...account.currency.map((entry) => entry.currency),
            ...balanceFields.flatMap((field) => account[field].map((entry) => entry.currency)),
          ]),
        ]
      : [account.currency];
  const balances = currencies.map((currency) => {
    const balance: NonNullable<AppAccount['balances']>[number] = { currency };
    for (const field of balanceFields) {
      const amount = toOptionalNumber(
        account.accountType === 'multi-currency'
          ? account[field].find((entry) => entry.currency === currency)?.amount
          : account[field],
      );
      if (amount !== undefined) balance[field] = amount;
    }
    return balance;
  });
  const identifiers = account.accountIdentifier ?? [];
  const identifierFields = [
    'accountNumber',
    'sortCode',
    'iban',
    'bic',
    'ledgerNumber',
    'aba',
    'ach',
    'bsbCode',
    'ifsc',
    'rtnCanada',
    'clabe',
    'bankCode',
    'branchCode',
  ] as const;
  const widgetAccount: AppAccount = {
    accountId: account.accountId,
    name: account.name,
    currency,
    availableBalance: toFiniteNumber(availableBalance),
    accountType: account.accountType,
    currencies,
    balances,
    identifiers: identifiers.map((identifier) => {
      const paymentSchemes = (['chaps', 'sepa', 'faster-payments', 'swift', 'ach', 'wire'] as const).flatMap(
        (scheme) => {
          const details = identifier.providerExtraInfo?.[scheme];
          if (!details || typeof details.currency !== 'string' || !details.currency) return [];
          const routing: Record<string, unknown> = details.routingCodes ?? {};
          return [
            {
              scheme,
              currency: details.currency,
              ...Object.fromEntries(
                Object.entries(details).filter(
                  ([field, value]) =>
                    ['country', 'accountNumber', 'iban', 'bankName', 'bankAddress'].includes(field) &&
                    typeof value === 'string',
                ),
              ),
              ...Object.fromEntries(
                Object.entries({
                  sortCode: routing['sort-code'],
                  bic: routing['bic'],
                  aba: routing['aba'],
                }).filter(([, value]) => typeof value === 'string'),
              ),
            },
          ];
        },
      );
      return {
        ...Object.fromEntries(
          identifierFields
            .filter((field) => typeof identifier[field] === 'string')
            .map((field) => [field, identifier[field]]),
        ),
        ...(paymentSchemes.length ? { paymentSchemes } : {}),
      };
    }),
    status: account.status,
    ...(account.parentAccountId ? { parentAccountId: account.parentAccountId } : {}),
  };
  const primary = identifiers.find(
    (identifier) => identifier.accountNumber || identifier.sortCode || identifier.iban,
  );
  const { accountNumber, sortCode, iban } = primary ?? {};
  if (accountNumber) widgetAccount.accountNumber = accountNumber;
  if (sortCode) widgetAccount.sortCode = sortCode;
  if (iban) widgetAccount.iban = iban;
  if (account.alias) widgetAccount.alias = account.alias;
  return widgetAccount;
}

export function toWidgetBeneficiary(beneficiary: GetBeneficiaryResponse): AppBeneficiary {
  const destination = beneficiary.destinationIdentifier;
  const widgetBeneficiary: AppBeneficiary = {
    beneficiaryId: beneficiary.id,
    name: beneficiary.name,
  };
  if (destination?.accountNumber) widgetBeneficiary.accountNumber = destination.accountNumber;
  if (destination?.sortCode) widgetBeneficiary.sortCode = destination.sortCode;
  if (destination?.iban) widgetBeneficiary.iban = destination.iban;
  if (destination?.currency) widgetBeneficiary.currency = destination.currency;
  if (beneficiary.nickName) widgetBeneficiary.nickname = beneficiary.nickName;
  return widgetBeneficiary;
}

export function toWidgetProfile(profile: ProfileStateModel | undefined): AppProfile {
  return {
    id: profile?.id ?? '',
    firstName: profile?.firstName,
    lastName: profile?.lastName,
    email: profile?.email,
    activeRole: profile?.activeRole,
  };
}

/**
 * The nominated account, flattened.
 *
 * The native shape carries `accountIdentifier` as an array of routing blocks, which the
 * platform never populates with more than one — there is exactly one nominated account per
 * organisation and one routing block on it. The array is collapsed rather than forwarded, and
 * `providerExtraInfo` (the provider's own per-scheme block) is dropped. Same mapping as the
 * BFF's `toWidgetNominatedAccount`.
 */
export function toWidgetNominatedAccount(
  account: NominatedAccountInfoDetailType,
): AppNominatedAccount {
  const identifier = account.accountIdentifier?.[0];
  const nominated: AppNominatedAccount = {
    accountId: account.accountId,
    currency: account.currency,
    country: account.country,
    status: account.status,
  };
  if (account.accountName) nominated.accountName = account.accountName;
  if (identifier?.accountNumber) nominated.accountNumber = identifier.accountNumber;
  if (identifier?.sortCode) nominated.sortCode = identifier.sortCode;
  if (identifier?.iban) nominated.iban = identifier.iban;
  if (identifier?.bic) nominated.bic = identifier.bic;
  if (account.pispInstitutionId) nominated.institutionId = account.pispInstitutionId;
  return nominated;
}

/**
 * `profile.update` mapped onto `PATCH /users`: the address is flattened
 * (`address.addressLine1` -> `address1`) as every other caller of that route sends it, and only
 * the fields present are written — the route patches rather than replaces.
 *
 * `emailVerified` / `phoneVerified` are never sent. The server's OTP gate is what decides
 * whether a new contact value counts as verified, and a widget asserting it would be asserting
 * exactly what the gate exists to establish.
 */
export function toProfileUpdatePayload(request: Partial<ProfileUpdateRequest>): Partial<UserProfileData> {
  const wire: Record<string, unknown> = {};
  if (request.firstName !== undefined) wire['firstName'] = request.firstName;
  if (request.middleName !== undefined) wire['middleName'] = request.middleName;
  if (request.lastName !== undefined) wire['lastName'] = request.lastName;
  if (request.dob !== undefined) wire['dob'] = request.dob;
  if (request.email !== undefined) wire['email'] = request.email;
  if (request.phone !== undefined) wire['phoneNumber'] = request.phone;
  const address = request.address;
  if (address) {
    if (address.country !== undefined) wire['country'] = address.country;
    if (address.addressLine1 !== undefined) wire['address1'] = address.addressLine1;
    if (address.addressLine2 !== undefined) wire['address2'] = address.addressLine2;
    if (address.addressLine3 !== undefined) wire['address3'] = address.addressLine3;
    if (address.addressLine4 !== undefined) wire['address4'] = address.addressLine4;
    if (address.city !== undefined) wire['city'] = address.city;
    if (address.county !== undefined) wire['county'] = address.county;
    if (address.postalCode !== undefined) wire['postalCode'] = address.postalCode;
  }
  if (request.marketingPreferences) {
    const preferences: Record<string, boolean> = {};
    for (const channel of ['email', 'sms', 'push', 'mail'] as const) {
      const value = request.marketingPreferences[channel];
      if (value !== undefined) preferences[channel] = value;
    }
    if (Object.keys(preferences).length) wire['marketingPreferences'] = preferences;
  }
  return wire as Partial<UserProfileData>;
}

export function toWidgetTransaction(transaction: TransactionWireItem): AppTransaction {
  return {
    transactionId: transaction.id ?? transaction.transactionNumber ?? '',
    amount: toFiniteNumber(transaction.amount),
    currency: transaction.currency,
    description: transaction.description,
    date: transaction.transactionDate,
    type: transaction.transactionType,
  };
}

/**
 * Flattens the current account's 'all' transaction bucket. Exchange and
 * scheduled variants live under their own AllTransactionType keys and are
 * not exposed to widgets.
 */
export function toWidgetTransactionList(
  state: TransactionStateType | undefined,
  currentAccountId: string,
): AppTransaction[] {
  const bucket = state?.accounts?.[currentAccountId]?.[TransactionType.ALL];
  return ((bucket?.result ?? []) as TransactionWireItem[]).map(toWidgetTransaction);
}

export function toPayee(request: CopCheckRequest): Payee {
  const payee: Payee = {
    name: request.name,
    accountType: request.accountType === 'business' ? ACCOUNTTYPE.BUSINESS : ACCOUNTTYPE.PERSONAL,
  };
  if (request.accountNumber) payee.accountNumber = request.accountNumber;
  if (request.sortCode) payee.sortCode = request.sortCode;
  if (request.iban) payee.iban = request.iban;
  return payee;
}

/**
 * CheckPayeeResponse.matchStatus is declared `true` but no caller reads it;
 * the real outcome is matchStatusCode holding a CheckPayeeMatchStatus value
 * (see beneficiary-form-actions.utils.ts). Codes where a check could not be
 * performed (bank not enrolled / no response / identifier scheme unsupported
 * / internal transfer) collapse to 'not_supported'; anything else that is not
 * a match is 'no_match' with the raw code as reasonCode.
 */
export function toWidgetTransferReason(reason: TransferReason): AppTransferReason {
  return { id: reason.id, description: reason.description };
}

export function toWidgetCard(card: CardInfoType): AppCard {
  // Deliberately a hand-picked allowlist: CardInfoType carries provider data
  // and address fields widgets must never see, and no PAN/CVV/PIN ever exists
  // on this model (those live behind the confirmation-gated secure endpoints).
  const widgetCard: AppCard = {
    cardId: card.id,
    status: card.status,
  };
  if (card.name) widgetCard.name = card.name;
  if (card.maskedPan) widgetCard.maskedPan = card.maskedPan;
  if (card.type) widgetCard.type = card.type;
  if (card.currency) widgetCard.currency = card.currency;
  return widgetCard;
}

function toWidgetRequirementRecord(
  source: Record<string, { requirement: string; regex?: string } | undefined>,
): Record<string, AppFieldRequirement> {
  const record: Record<string, AppFieldRequirement> = {};
  for (const [field, value] of Object.entries(source)) {
    if (!value?.requirement) continue;
    record[field] = { requirement: value.requirement as AppFieldRequirement['requirement'] };
    if (value.regex) record[field].regex = value.regex;
  }
  return record;
}

/**
 * Flattens BeneficiaryRequirementsResponse onto the contract shape: the three
 * identifier fields plus the routingCodes map become requirement records, and
 * the nested `requirements.beneficiaryAddress` flag becomes addressRequirement.
 */
export function toWidgetBeneficiaryRequirements(
  response: BeneficiaryRequirementsResponse,
): AppBeneficiaryRequirements {
  const bankAccount = response.beneficiaryBankAccount;
  const requirements: AppBeneficiaryRequirements = {
    bankAccount: {
      fields: toWidgetRequirementRecord({
        iban: bankAccount?.iban,
        accountNumber: bankAccount?.accountNumber,
        bankName: bankAccount?.bankName,
      }),
      routingCodes: toWidgetRequirementRecord(bankAccount?.routingCodes ?? {}),
      addressRequirement: bankAccount?.requirements?.beneficiaryAddress ?? 'optional',
    },
  };
  if (response.beneficiaryAddress) {
    requirements.address = toWidgetRequirementRecord(response.beneficiaryAddress);
  }
  return requirements;
}

/**
 * Maps the contract create-request onto AddBeneficiaryRequest. The frontend
 * DTO requires destinationIdentifier.country; when the widget omits it the
 * IBAN's country prefix is used (mirrors
 * getCountryCodeFromBeneficiaryAccountDetail), falling back to GB — the same
 * default the native beneficiary flows apply.
 */
export function toAddBeneficiaryRequest(request: BeneficiaryCreateRequest): AddBeneficiaryRequest {
  const country = request.country ?? (request.iban ? request.iban.slice(0, 2).toUpperCase() : 'GB');
  const destinationIdentifier: BeneficiaryDestinationIdentifier = {
    country,
    currency: request.currency,
  };
  if (request.accountNumber) destinationIdentifier.accountNumber = request.accountNumber;
  if (request.sortCode) destinationIdentifier.sortCode = request.sortCode;
  if (request.iban) destinationIdentifier.iban = request.iban;

  const addRequest: AddBeneficiaryRequest = {
    name: request.name,
    accountType: request.accountType === 'business' ? ACCOUNTTYPE.BUSINESS : ACCOUNTTYPE.PERSONAL,
    destinationIdentifier,
  };
  if (request.nickname) addRequest.nickName = request.nickname;
  return addRequest;
}

/**
 * A savings application that is not an account yet.
 *
 * `applicationReference` is the platform's zero-padded customer-facing reference
 * (the entity's `formattedApplicationReference` getter), which is what the
 * contract calls `applicationNumber` — the raw integer column is never crossed.
 *
 * There is no `createdAt` here because `account_open_request` has no timestamp
 * column at all; deriving one from the reference sequence would be a guess, and a
 * migration to add one is a decision of its own.
 */
export function toWidgetSavingsAccountRequest(
  request: AccountOpenRequestType,
): AppSavingsAccountRequest {
  const widgetRequest: AppSavingsAccountRequest = {
    requestId: request.id,
    status: request.status,
  };
  if (request.applicationReference) widgetRequest.applicationNumber = request.applicationReference;
  const details = request.additionalDetails;
  if (details?.accountName) widgetRequest.accountName = details.accountName;
  if (details?.currency) widgetRequest.currency = details.currency;
  const savings = details?.savings;
  if (savings?.productCode) widgetRequest.productCode = savings.productCode;
  if (savings?.accountType) widgetRequest.accountType = savings.accountType;
  if (savings?.termMonths !== undefined) widgetRequest.termMonths = savings.termMonths;
  if (savings?.interestRate !== undefined) widgetRequest.interestRate = savings.interestRate;
  return widgetRequest;
}

/**
 * Wire balances on SavingsAccountInfoType are strings and start/end dates are
 * null for open-ended (easy-access) accounts; nulls are omitted rather than
 * forwarded.
 */
export function toWidgetSavingsAccount(account: SavingsAccountInfoType): AppSavingsAccount {
  const widgetAccount: AppSavingsAccount = {
    savingsAccountId: account.accountId,
    currency: account.currency,
    balance: toFiniteNumber(account.balance),
  };
  const availableBalance = toOptionalNumber(account.availableBalance);
  if (availableBalance !== undefined) widgetAccount.availableBalance = availableBalance;
  if (account.productName) widgetAccount.productName = account.productName;
  const documents: NonNullable<AppSavingsAccount['documents']> = {};
  if (account.documents?.keyProductInformationUrl) {
    documents.keyProductInformationUrl = account.documents.keyProductInformationUrl;
  }
  if (account.documents?.termsAndConditionsUrl) {
    documents.termsAndConditionsUrl = account.documents.termsAndConditionsUrl;
  }
  if (account.documents?.interestRateTermsUrl) {
    documents.interestRateTermsUrl = account.documents.interestRateTermsUrl;
  }
  if (Object.keys(documents).length) widgetAccount.documents = documents;
  if (account.accountType) widgetAccount.accountType = account.accountType;
  if (account.interestRate !== undefined && account.interestRate !== null) {
    widgetAccount.interestRate = account.interestRate;
  }
  if (account.startDate) widgetAccount.startDate = account.startDate;
  if (account.endDate) widgetAccount.endDate = account.endDate;
  if (account.status) widgetAccount.status = account.status;
  // The capability flags cross as published: absent stays absent (the
  // platform's guards only block on an explicit false, and a widget must be
  // able to tell "not blocked" from "not said").
  if (account.isFundingEnabled !== undefined) widgetAccount.isFundingEnabled = account.isFundingEnabled;
  if (account.isWithdrawalEnabled !== undefined) {
    widgetAccount.isWithdrawalEnabled = account.isWithdrawalEnabled;
  }
  if (account.isClosureEnabled !== undefined) widgetAccount.isClosureEnabled = account.isClosureEnabled;
  if (account.isRenewalEnabled !== undefined) widgetAccount.isRenewalEnabled = account.isRenewalEnabled;

  // Everything below crossed the platform's own SavingsAccountInfoType all along
  // and was dropped here until 2026-08-29. Wire money fields are strings and the
  // provider-absent ones are null, so every one goes through the same
  // omit-rather-than-forward rule as availableBalance above.
  if (account.productCode) widgetAccount.productCode = account.productCode;
  if (account.accountName) widgetAccount.accountName = account.accountName;
  if (account.country) widgetAccount.country = account.country;

  // The pot's own routing block. `accountIdentifier` is an array on the native
  // type but the platform never populates a second element for a savings
  // account, so it is collapsed rather than forwarded — the same call
  // toWidgetNominatedAccount makes, for the same reason. providerExtraInfo is
  // deliberately not crossed: it is the provider's per-scheme routing detail.
  const identifier = account.accountIdentifier?.[0];
  if (identifier?.accountNumber) widgetAccount.accountNumber = identifier.accountNumber;
  if (identifier?.sortCode) widgetAccount.sortCode = identifier.sortCode;
  if (identifier?.iban) widgetAccount.iban = identifier.iban;
  if (identifier?.bic) widgetAccount.bic = identifier.bic;

  const interestAccrued = toOptionalNumber(account.interestAccrued);
  if (interestAccrued !== undefined) widgetAccount.interestAccrued = interestAccrued;
  const interestAtMaturity = toOptionalNumber(account.interestAtMaturity);
  if (interestAtMaturity !== undefined) widgetAccount.interestAtMaturity = interestAtMaturity;
  const balanceAtMaturity = toOptionalNumber(account.balanceAtMaturity);
  if (balanceAtMaturity !== undefined) widgetAccount.balanceAtMaturity = balanceAtMaturity;
  const deposits = toOptionalNumber(account.deposits);
  if (deposits !== undefined) widgetAccount.deposits = deposits;
  const accountMaxDeposit = toOptionalNumber(account.maxDeposit);
  if (accountMaxDeposit !== undefined) widgetAccount.maxDeposit = accountMaxDeposit;
  const accountMinBalance = toOptionalNumber(account.minBalance);
  if (accountMinBalance !== undefined) widgetAccount.minBalance = accountMinBalance;
  if (account.annualEquivalantInterestRate !== undefined && account.annualEquivalantInterestRate !== null) {
    widgetAccount.aer = account.annualEquivalantInterestRate;
  }
  if (account.termMonths !== undefined) widgetAccount.termMonths = account.termMonths;
  if (account.lastFundingDate) widgetAccount.lastFundingDate = account.lastFundingDate;

  // `maturityWindowOpensAt` has no column of its own on the native type: the
  // universal-sandbox adapter parks it in `metadata`, which is `unknown`-valued,
  // so it is read defensively rather than cast.
  const maturityWindowOpensAt = account.metadata?.['maturityWindowOpensAt'];
  if (typeof maturityWindowOpensAt === 'string' && maturityWindowOpensAt) {
    widgetAccount.maturityWindowOpensAt = maturityWindowOpensAt;
  }

  // Forwarded whole for debugging parity with v1; see AppSavingsAccount.metadata.
  if (account.metadata && typeof account.metadata === 'object' && !Array.isArray(account.metadata)) {
    widgetAccount.metadata = account.metadata;
  }

  if (account.withdrawalAllowance) {
    const allowance: AppSavingsWithdrawalAllowance = {
      limit: toFiniteNumber(account.withdrawalAllowance.limit),
    };
    const used = toOptionalNumber(account.withdrawalAllowance.used);
    if (used !== undefined) allowance.used = used;
    const remaining = toOptionalNumber(account.withdrawalAllowance.remaining);
    if (remaining !== undefined) allowance.remaining = remaining;
    if (account.withdrawalAllowance.period) allowance.period = account.withdrawalAllowance.period;
    if (account.withdrawalAllowance.resetsAt) allowance.resetsAt = account.withdrawalAllowance.resetsAt;
    widgetAccount.withdrawalAllowance = allowance;
  }
  return widgetAccount;
}

/** minBalance/maxDeposit arrive as provider strings and are parsed to numbers. */
export function toWidgetSavingsProduct(product: SavingsProductType): AppSavingsProduct {
  const widgetProduct: AppSavingsProduct = {
    productCode: product.productCode,
    productName: product.productName,
  };
  if (product.accountType) widgetProduct.accountType = product.accountType;
  if (product.currency) widgetProduct.currency = product.currency;
  if (product.interestRate !== undefined && product.interestRate !== null) {
    widgetProduct.interestRate = product.interestRate;
  }
  if (product.aer !== undefined && product.aer !== null) widgetProduct.aer = product.aer;
  if (product.termMonths !== undefined) widgetProduct.termMonths = product.termMonths;
  const minBalance = toOptionalNumber(product.minBalance);
  if (minBalance !== undefined) widgetProduct.minBalance = minBalance;
  const maxDeposit = toOptionalNumber(product.maxDeposit);
  if (maxDeposit !== undefined) widgetProduct.maxDeposit = maxDeposit;
  if (product.description) widgetProduct.description = product.description;
  if (product.expiry) widgetProduct.expiry = product.expiry;
  // The literature URLs cross as published, and only the ones that are there:
  // an empty `documents: {}` would read to a widget as "this product has no
  // documents" rather than "this provider does not publish them".
  const documents: AppSavingsProductDocuments = {};
  if (product.documents?.keyProductInformationUrl) {
    documents.keyProductInformationUrl = product.documents.keyProductInformationUrl;
  }
  if (product.documents?.termsAndConditionsUrl) {
    documents.termsAndConditionsUrl = product.documents.termsAndConditionsUrl;
  }
  if (product.documents?.interestRateTermsUrl) {
    documents.interestRateTermsUrl = product.documents.interestRateTermsUrl;
  }
  if (Object.keys(documents).length > 0) widgetProduct.documents = documents;
  // ThisBank publishes the withdrawal cap as `maxNrOfWithdrawal` inside its
  // untyped `providerExtraInfo` blob (the raw product, spread wholesale by
  // thisbank-savings.service.ts). Read defensively: the blob is
  // `Record<string, unknown>`-shaped by declaration and provider-shaped in fact.
  const maxWithdrawals = toOptionalNumber(
    product.providerExtraInfo?.['maxNrOfWithdrawal'] as string | number | null | undefined,
  );
  if (maxWithdrawals !== undefined) widgetProduct.maxWithdrawalsPerYear = maxWithdrawals;
  return widgetProduct;
}

function toWidgetTransactionParty(
  party: TransactionDetailResponse['sender'] | TransactionDetailResponse['receiver'] | undefined,
): AppTransactionParty | undefined {
  if (!party) return undefined;
  const widgetParty: AppTransactionParty = { name: party.name };
  if (party.accountNumber) widgetParty.accountNumber = party.accountNumber;
  if (party.sortCode) widgetParty.sortCode = party.sortCode;
  if ('iban' in party && party.iban) widgetParty.iban = party.iban;
  return widgetParty;
}

/** `reference` prefers the wire reference, falling back to paymentReference. */
export function toWidgetTransactionDetail(response: TransactionDetailResponse): AppTransactionDetail {
  const detail: AppTransactionDetail = {
    transactionId: response.id,
    amount: toFiniteNumber(response.amount),
  };
  if (response.currency) detail.currency = response.currency;
  if (response.dateOfTransaction) detail.date = response.dateOfTransaction;
  if (response.postingDate) detail.postingDate = response.postingDate;
  const reference = response.reference ?? response.paymentReference;
  if (reference) detail.reference = reference;
  if (response.type) detail.type = response.type;
  if (response.status) detail.status = response.status;
  if (response.feeAmount !== undefined) detail.feeAmount = toFiniteNumber(response.feeAmount);
  if (response.feeCurrency) detail.feeCurrency = response.feeCurrency;
  if (response.totalAmount !== undefined) detail.totalAmount = toFiniteNumber(response.totalAmount);
  const sender = toWidgetTransactionParty(response.sender);
  if (sender) detail.sender = sender;
  const receiver = toWidgetTransactionParty(response.receiver);
  if (receiver) detail.receiver = receiver;
  return detail;
}

/**
 * The wire item nests everything except the payment id under `data`, plus the
 * job's own fire time as `startAfter` — the pg-boss `start_after`, which for
 * these reversal-window rows is the instant the window closes and the
 * transfer is sent, i.e. the contract's `expiryReversalAt`. A future-dated
 * payment riding the window additionally carries the original request's
 * `schedulePaymentDate` inside `data`.
 */
export function toWidgetScheduledPayment(payment: CancellablePaymentResponse): AppScheduledPayment {
  const widgetPayment: AppScheduledPayment = {
    paymentId: payment.id,
    amount: toFiniteNumber(payment.data?.amount),
  };
  if (payment.data?.currency) widgetPayment.currency = payment.data.currency;
  if (payment.data?.name) widgetPayment.beneficiaryName = payment.data.name;
  if (payment.data?.paymentReference) widgetPayment.reference = payment.data.paymentReference;
  if (payment.data?.sourceAccountId) widgetPayment.sourceAccountId = payment.data.sourceAccountId;
  if (payment.startAfter) widgetPayment.expiryReversalAt = String(payment.startAfter);
  if (payment.data?.schedulePaymentDate) {
    widgetPayment.scheduledAt = String(payment.data.schedulePaymentDate);
  }
  return widgetPayment;
}

/** List rows: `status` is the wire approverStatus; dates are stringified. */
export function toWidgetPaymentRequest(request: PaymentRequestListResponse): AppPaymentRequest {
  const widgetRequest: AppPaymentRequest = {
    paymentRequestId: request.id,
    amount: toFiniteNumber(request.amount),
    currency: request.currency,
  };
  if (request.paymentReference) widgetRequest.reference = request.paymentReference;
  if (request.paymentReason) widgetRequest.reason = request.paymentReason;
  if (request.approverStatus) widgetRequest.status = request.approverStatus;
  if (request.createdDate) widgetRequest.createdDate = String(request.createdDate);
  if (request.approvedOrRejectedDate) {
    widgetRequest.approvedOrRejectedDate = String(request.approvedOrRejectedDate);
  }
  if (request.requester?.name) widgetRequest.requesterName = request.requester.name;
  if (request.approver?.name) widgetRequest.approverName = request.approver.name;
  if (request.beneficiary?.name) widgetRequest.beneficiaryName = request.beneficiary.name;
  if (request.account?.id) widgetRequest.accountId = request.account.id;
  if (request.account?.name) widgetRequest.accountName = request.account.name;
  if (request.orgId) widgetRequest.organisationId = request.orgId;
  if (request.orgName) widgetRequest.organisationName = request.orgName;
  return widgetRequest;
}

/**
 * Detail rows (PaymentRequestSummary): amount is a wire string and the source
 * account rides on the requester; `status` keeps the summary's own status.
 */
export function toWidgetPaymentRequestDetail(summary: PaymentRequestSummary): AppPaymentRequest {
  const widgetRequest: AppPaymentRequest = {
    paymentRequestId: summary.id,
    amount: toFiniteNumber(summary.amount),
    currency: summary.currency,
  };
  if (summary.paymentReference) widgetRequest.reference = summary.paymentReference;
  if (summary.paymentReason) widgetRequest.reason = summary.paymentReason;
  if (summary.status) widgetRequest.status = summary.status;
  if (summary.createdDate) widgetRequest.createdDate = String(summary.createdDate);
  if (summary.approvedOrRejectedDate) {
    widgetRequest.approvedOrRejectedDate = String(summary.approvedOrRejectedDate);
  }
  if (summary.requester?.name) widgetRequest.requesterName = summary.requester.name;
  if (summary.approver?.name) widgetRequest.approverName = summary.approver.name;
  if (summary.beneficiary?.name) widgetRequest.beneficiaryName = summary.beneficiary.name;
  if (summary.requester?.accountId) widgetRequest.accountId = summary.requester.accountId;
  if (summary.requester?.accountName) widgetRequest.accountName = summary.requester.accountName;
  return widgetRequest;
}

/** CardLimitsType nests string amounts under `amount`; all are parsed. */
export function toWidgetCardLimit(limit: CardLimitsType): AppCardLimit {
  const widgetLimit: AppCardLimit = {
    limitType: limit.limitType,
    trxGroup: limit.trxGroup,
    limit: toFiniteNumber(limit.amount?.value),
  };
  if (limit.amount?.currency) widgetLimit.currency = limit.amount.currency;
  const spent = toOptionalNumber(limit.amount?.spendValue);
  if (spent !== undefined) widgetLimit.spent = spent;
  const available = toOptionalNumber(limit.amount?.availableValue);
  if (available !== undefined) widgetLimit.available = available;
  return widgetLimit;
}

/**
 * `extraAttributes` is a provider/product-specific bag (depositAmount,
 * sourceOfFunds, ...) forwarded verbatim; `accountName` is only sent when the
 * widget supplied one so the provider keeps its own default naming.
 */
export function toCreateSavingsAccountRequest(
  request: SavingsAccountCreateRequest,
): CreateSavingsAccountRequest {
  const createRequest: CreateSavingsAccountRequest = {
    accountType: request.accountType,
    productCode: request.productCode,
    currency: request.currency,
  };
  if (request.accountName) createRequest.accountName = request.accountName;
  if (request.extraAttributes) createRequest.extraAttributes = request.extraAttributes;
  return createRequest;
}

/**
 * The wire response nests everything under `data` and is an *open request*, not
 * necessarily an account: `accountId` only lands on approval, otherwise
 * `requestId` identifies the pending application. `sumsubAccessToken` is a KYC
 * credential and is deliberately dropped.
 */
export function toSavingsAccountCreateResult(
  data: SavingsAccountInfoTypeResponse | undefined,
): SavingsAccountCreateResult {
  const result: SavingsAccountCreateResult = { status: data?.status ?? '' };
  if (data?.accountId) result.savingsAccountId = data.accountId;
  if (data?.requestId) result.requestId = data.requestId;
  if (data?.applicationNumber) result.applicationNumber = data.applicationNumber;
  return result;
}

/**
 * The wire payload takes `amount` as a string and calls the fixed side
 * `fixedType`. buyCurrency/sellCurrency are only sent when supplied — they are
 * required for multi-currency wallets, where both account ids are the same.
 */
export function toExchangeQuotePayload(request: ExchangeQuoteRequest): ExchangeQuotePayload {
  const payload: ExchangeQuotePayload = {
    buyAccountId: request.buyAccountId,
    sellAccountId: request.sellAccountId,
    amount: String(request.amount),
    fixedType: request.fixedSide === 'buy' ? ExchangeFixedType.BUY : ExchangeFixedType.SELL,
  };
  if (request.buyCurrency) payload.buyCurrency = request.buyCurrency;
  if (request.sellCurrency) payload.sellCurrency = request.sellCurrency;
  return payload;
}

/** `expiryDateTime` becomes the contract's `expiresAt` deadline. */
export function toExchangeQuoteResult(response: ExchangeQuoteResponse): ExchangeQuoteResult {
  const result: ExchangeQuoteResult = {
    quoteId: response.quoteId,
    buyCurrency: response.buyCurrency,
    buyAmount: toFiniteNumber(response.buyAmount),
    sellCurrency: response.sellCurrency,
    sellAmount: toFiniteNumber(response.sellAmount),
    exchangeRate: toFiniteNumber(response.exchangeRate),
    expiresAt: response.expiryDateTime,
    fixedSide: response.fixedSide,
  };
  if (response.cutOffTimeInMinutes !== undefined) {
    result.cutOffTimeInMinutes = response.cutOffTimeInMinutes;
  }
  const feeAmount = toOptionalNumber(response.feeAmount);
  if (feeAmount !== undefined) result.feeAmount = feeAmount;
  if (response.feeCurrency) result.feeCurrency = response.feeCurrency;
  if (response.exchangeDate) result.exchangeDate = response.exchangeDate;
  if (response.settlementDate) result.settlementDate = response.settlementDate;
  const debitAmount = toOptionalNumber(response.debitAmount);
  if (debitAmount !== undefined) result.debitAmount = debitAmount;
  const creditAmount = toOptionalNumber(response.creditAmount);
  if (creditAmount !== undefined) result.creditAmount = creditAmount;
  return result;
}

/**
 * Provider-side figures (serviceProviderBuyAmount/-SellAmount/-Rate), the raw
 * account ids and the sender/receiver identifier blocks are host-internal and
 * stay off the contract; fee fields are nullable on the wire.
 */
export function toExchangeAcceptResult(response: ExchangeTransactionResponse): ExchangeAcceptResult {
  const result: ExchangeAcceptResult = {
    exchangeId: response.id,
    quoteId: response.quoteId,
    buyCurrency: response.buyCurrency,
    buyAmount: toFiniteNumber(response.buyAmount),
    sellCurrency: response.sellCurrency,
    sellAmount: toFiniteNumber(response.sellAmount),
    exchangeRate: toFiniteNumber(response.exchangeRate),
  };
  if (response.transactionNumber) result.transactionNumber = response.transactionNumber;
  if (response.status) result.status = response.status;
  const feeAmount = toOptionalNumber(response.feeAmount);
  if (feeAmount !== undefined) result.feeAmount = feeAmount;
  if (response.feeCurrency) result.feeCurrency = response.feeCurrency;
  if (response.exchangeDate) result.exchangeDate = response.exchangeDate;
  if (response.settlementDate) result.settlementDate = response.settlementDate;
  const debitAmount = toOptionalNumber(response.debitAmount);
  if (debitAmount !== undefined) result.debitAmount = debitAmount;
  const creditAmount = toOptionalNumber(response.creditAmount);
  if (creditAmount !== undefined) result.creditAmount = creditAmount;
  return result;
}

/**
 * POST /payments/request (PaymentsRequestDTO) names the beneficiary `bid` and
 * demands a non-empty paymentReference, transferReasonId and
 * transferReasonIdText — the caller (createPaymentRequest in
 * custom-widget-api-operations.ts) validates reference/transferReasonId before
 * mapping and resolves `transferReasonIdText` (the reason's human-readable
 * label, which the contract deliberately does not carry) from the same
 * transfer-reasons catalogue payments.reasons.list serves. `sourceAccountId`
 * is resolved by the caller too (native parity: paymentRequestProcessing sends
 * the currently selected account).
 */
export function toPaymentRequestorRequest(
  request: PaymentRequestCreateRequest,
  sourceAccountId: string,
  transferReasonIdText: string,
): PaymentRequestorRequest {
  return {
    sourceAccountId,
    bid: request.beneficiaryId,
    amount: Number(request.amount),
    currency: request.currency,
    paymentReference: request.reference ?? '',
    transferReasonId: request.transferReasonId ?? '',
    transferReasonIdText,
  };
}

/**
 * International beneficiaries go to the same POST /beneficiaries endpoint as
 * domestic ones; the routing identifiers are flat siblings inside
 * `destinationIdentifier` (there is no routingCodes object on the wire) and the
 * address is a sibling of it. Empty strings are dropped so a widget cannot
 * push a blank identifier past the provider's validators.
 */
export function toAddInternationalBeneficiaryRequest(
  request: InternationalBeneficiaryCreateRequest,
): AddBeneficiaryRequest {
  const country = request.country ?? (request.iban ? request.iban.slice(0, 2).toUpperCase() : 'GB');
  const destinationIdentifier: BeneficiaryDestinationIdentifier = {
    country,
    currency: request.currency,
  };
  const identifiers: (keyof BeneficiaryDestinationIdentifier &
    keyof InternationalBeneficiaryCreateRequest)[] = [
    'iban',
    'accountNumber',
    'sortCode',
    'bankName',
    'bic',
    'aba',
    'rtnCanada',
    'clabe',
    'bsbCode',
    'ifsc',
    'cnaps',
    'bankCode',
    'branchCode',
  ];
  for (const field of identifiers) {
    const value = request[field];
    if (value) destinationIdentifier[field] = String(value);
  }

  const addRequest: AddBeneficiaryRequest = {
    name: request.name,
    accountType: request.accountType === 'business' ? ACCOUNTTYPE.BUSINESS : ACCOUNTTYPE.PERSONAL,
    destinationIdentifier,
  };
  if (request.nickname) addRequest.nickName = request.nickname;
  if (request.address) {
    const address: BeneficiaryIdentifierAddress = {
      addressLine1: request.address.addressLine1,
      city: request.address.city,
      country: request.address.country,
    };
    if (request.address.addressLine2) address.addressLine2 = request.address.addressLine2;
    if (request.address.addressLine3) address.addressLine3 = request.address.addressLine3;
    if (request.address.addressLine4) address.addressLine4 = request.address.addressLine4;
    if (request.address.state) address.state = request.address.state;
    if (request.address.postCode) address.postCode = request.address.postCode;
    addRequest.address = address;
  }
  return addRequest;
}

/**
 * International payments share TransferMoneyRequest — and the /payments
 * endpoint — with domestic ones. `review` is the only field that distinguishes
 * a dry-run precheck (true) from the real submission (false); it is always sent
 * explicitly because the backend treats absent as false.
 */
export function toInternationalTransferMoneyRequest(
  request: InternationalPaymentRequest,
  review: boolean,
): TransferMoneyRequest {
  return {
    sourceAccountId: request.sourceAccountId,
    bid: request.beneficiaryId,
    amount: Number(request.amount),
    paymentReference: request.reference ?? '',
    transferReasonId: request.transferReasonId ?? '',
    review,
  };
}

/**
 * TransferMoneyInternationalResponse declares every field required, but the
 * body is really the domestic TransferMoneyResponse (which types them all
 * optional) so nothing is assumed present beyond amount/currency.
 */
export function toInternationalPaymentPrecheckResult(
  response: TransferMoneyInternationalResponse | undefined,
): InternationalPaymentPrecheckResult {
  const result: InternationalPaymentPrecheckResult = {
    amount: toFiniteNumber(response?.amount),
    currency: response?.currency ?? '',
  };
  const feeAmount = toOptionalNumber(response?.feeAmount);
  if (feeAmount !== undefined) result.feeAmount = feeAmount;
  const totalAmount = toOptionalNumber(response?.totalAmount);
  if (totalAmount !== undefined) result.totalAmount = totalAmount;
  if (response?.receiverName) result.receiverName = response.receiverName;
  if (response?.receiverAccountNumber) result.receiverAccountNumber = response.receiverAccountNumber;
  if (response?.senderName) result.senderName = response.senderName;
  if (response?.senderAccountNumber) result.senderAccountNumber = response.senderAccountNumber;
  return result;
}

/** As above plus the payment id, which the narrower intl wire type omits. */
export function toInternationalPaymentResult(
  response: (TransferMoneyInternationalResponse & { id?: string }) | undefined,
): InternationalPaymentResult {
  const result: InternationalPaymentResult = toInternationalPaymentPrecheckResult(response);
  if (response?.id) result.transactionId = response.id;
  return result;
}

/**
 * Bulk-payment batches: `totalAmount` is a wire string, the dates are
 * snake_case, and `accountCurrency` is the batch currency. The internal
 * `fileId`, `userId` and `InactiveBeneficiaryCheck` fields stay host-only.
 */
export function toWidgetBulkPayment(payment: BulkPaymentHistoryResult): AppBulkPayment {
  const widgetPayment: AppBulkPayment = {
    bulkPaymentId: payment.id,
    status: payment.status,
    totalAmount: toFiniteNumber(payment.totalAmount),
  };
  if (payment.fileName) widgetPayment.fileName = payment.fileName;
  if (payment.accountId) widgetPayment.accountId = payment.accountId;
  if (payment.accountName) widgetPayment.accountName = payment.accountName;
  if (payment.accountCurrency) widgetPayment.currency = payment.accountCurrency;
  if (payment.totalPayments !== undefined) widgetPayment.totalPayments = payment.totalPayments;
  if (payment.totalSuccessPayments !== undefined) {
    widgetPayment.totalSuccessPayments = payment.totalSuccessPayments;
  }
  if (payment.totalFailedPayments !== undefined) {
    widgetPayment.totalFailedPayments = payment.totalFailedPayments;
  }
  if (payment.totalDuplicatePayments !== undefined) {
    widgetPayment.totalDuplicatePayments = payment.totalDuplicatePayments;
  }
  if (payment.totalSchedulePayments !== undefined) {
    widgetPayment.totalSchedulePayments = payment.totalSchedulePayments;
  }
  if (payment.totalUniqueBeneficiaries !== undefined) {
    widgetPayment.totalUniqueBeneficiaries = payment.totalUniqueBeneficiaries;
  }
  if (payment.totalErrors !== undefined) widgetPayment.totalErrors = payment.totalErrors;
  if (payment.created_at) widgetPayment.createdDate = payment.created_at;
  if (payment.executed_at) widgetPayment.executedDate = payment.executed_at;
  return widgetPayment;
}

/**
 * One transfer row. `amount` is a wire string. From the row's
 * beneficiaryBankAccounts block (snake_cased) only the account number, sort
 * code, IBAN and BIC are forwarded, as V1 showed them; the beneficiary's
 * address stays host-only. The CoP fields and the client-side
 * `entityStyle`/`actionEnabled` decorations are host-only too.
 */
export function toWidgetBulkPaymentTransfer(transfer: BulkPaymentResultList): AppBulkPaymentTransfer {
  const widgetTransfer: AppBulkPaymentTransfer = {
    transferId: transfer.id,
    beneficiaryName: transfer.name,
    amount: toFiniteNumber(transfer.amount),
    currency: transfer.currency,
    status: transfer.status,
  };
  if (transfer.beneficiaryId) widgetTransfer.beneficiaryId = transfer.beneficiaryId;
  if (transfer.transactionStatus) widgetTransfer.transactionStatus = transfer.transactionStatus;
  if (transfer.transactionId) widgetTransfer.transactionId = transfer.transactionId;
  if (transfer.transactionReference) widgetTransfer.reference = transfer.transactionReference;
  if (transfer.scheduleType) widgetTransfer.scheduleType = transfer.scheduleType;
  if (transfer.scheduleDate) widgetTransfer.scheduleDate = transfer.scheduleDate;
  if (transfer.row) widgetTransfer.row = transfer.row;
  if (transfer.isDuplicate !== undefined) widgetTransfer.isDuplicate = transfer.isDuplicate;
  if (transfer.isError !== undefined) widgetTransfer.isError = transfer.isError;
  const bank = transfer.beneficiaryBankAccounts?.[0];
  if (bank?.account_number) widgetTransfer.accountNumber = bank.account_number;
  if (bank?.sort_code) widgetTransfer.sortCode = bank.sort_code;
  if (bank?.iban) widgetTransfer.iban = bank.iban;
  if (bank?.bic) widgetTransfer.bic = bank.bic;
  return widgetTransfer;
}

/**
 * Batch totals. Here `totalAmount` is already a number and the date is
 * camelCase `executedAt` — the opposite of the listing row, so both are handled
 * separately rather than shared. `recordId` (the payment-request record the
 * native approval flow PATCHes) is host-only.
 */
export function toWidgetBulkPaymentSummary(summary: BulkPaymentsSummary): AppBulkPaymentSummary {
  const widgetSummary: AppBulkPaymentSummary = { status: summary.masterStatus ?? '' };
  const totalAmount = toOptionalNumber(summary.totalAmount);
  if (totalAmount !== undefined) widgetSummary.totalAmount = totalAmount;
  if (summary.currency) widgetSummary.currency = summary.currency;
  if (summary.accountId) widgetSummary.accountId = summary.accountId;
  if (summary.accountName) widgetSummary.accountName = summary.accountName;
  if (summary.totalPayments !== undefined) widgetSummary.totalPayments = summary.totalPayments;
  if (summary.totalSuccessPayments !== undefined) {
    widgetSummary.totalSuccessPayments = summary.totalSuccessPayments;
  }
  if (summary.totalFailedPayments !== undefined) {
    widgetSummary.totalFailedPayments = summary.totalFailedPayments;
  }
  if (summary.totalDuplicatePayments !== undefined) {
    widgetSummary.totalDuplicatePayments = summary.totalDuplicatePayments;
  }
  if (summary.totalSchedulePayments !== undefined) {
    widgetSummary.totalSchedulePayments = summary.totalSchedulePayments;
  }
  if (summary.totalPendingPayments !== undefined) {
    widgetSummary.totalPendingPayments = summary.totalPendingPayments;
  }
  if (summary.totalUniqueBeneficiaries !== undefined) {
    widgetSummary.totalUniqueBeneficiaries = summary.totalUniqueBeneficiaries;
  }
  if (summary.totalErrors !== undefined) widgetSummary.totalErrors = summary.totalErrors;
  if (summary.executedAt) widgetSummary.executedDate = String(summary.executedAt);
  return widgetSummary;
}

export function toWidgetPaymentScheme(scheme: {
  label: string;
  value: PaymentRailOptions;
}): AppPaymentScheme {
  return { label: scheme.label, value: scheme.value };
}

export function toCopCheckResult(response: CheckPayeeResponse): CopCheckResult {
  const code = response.matchStatusCode;
  switch (code) {
    case CheckPayeeMatchStatus.MATCHED:
    case CheckPayeeMatchStatus.NAME_MATCHED:
      return { status: 'match' };
    case CheckPayeeMatchStatus.CLOSE_MATCH:
    case CheckPayeeMatchStatus.ACCOUNT_TYPE_MISMATCH_NAME_CLOSE_MATCH:
      return { status: 'close_match', actualName: response.name };
    case CheckPayeeMatchStatus.ACCOUNT_NOT_SUPPORTED:
    case CheckPayeeMatchStatus.SORT_CODE_NOT_SUPPORTED:
    case CheckPayeeMatchStatus.NO_RESPONSE:
    case CheckPayeeMatchStatus.NOT_ENROLLED:
    case CheckPayeeMatchStatus.INTERNAL:
      return { status: 'not_supported', reasonCode: code };
    default:
      return { status: 'no_match', reasonCode: code };
  }
}

/* ───────────────────────────── onboarding ─────────────────────────────── */

/**
 * Onboarding mappers. Two directions, both narrow on purpose:
 *
 * - *outbound* (widget -> wire): the widget's structured payload becomes the flat
 *   shape the onboarding endpoints accept. This is where the platform's naming
 *   ends up (`phoneNumber` not `phone`, `address1` not `addressLine1`,
 *   `county` for a person and `state` for a company) so a widget never has to
 *   learn any of it.
 * - *inbound* (platform -> widget): profile state and host-held verification
 *   become the one normalised `onboardingStatus` payload.
 *
 * Nothing here reaches the network, holds a credential, or decides whether a
 * submit is allowed — the gate lives in the shared SDK rules
 * (app-sdk onboarding.ts) so the mock host enforces exactly the same thing.
 */

/** Where the platform keeps its onboarding verification markers. */
export interface OnboardingVerificationMarkers {
  isEmailVerified?: boolean | string;
  emailVerifiedAt?: string | null;
  verifiedEmail?: string | null;
  isPhoneVerified?: boolean | string;
  phoneVerifiedAt?: string | null;
  verifiedPhone?: string | null;
}

/**
 * Reads the platform's own onboarding form-state markers into the shape the
 * shared rules take.
 *
 * These are deliberately the *same* fields the native onboarding CTA reads
 * (`isEmailVerified` / `emailVerifiedAt` / `verifiedEmail`), not a parallel
 * store: a customer who verified on a native page and then reached a custom
 * widget — or the reverse — must not be asked to verify twice, and the submit
 * gate must agree with the one the native flow applies.
 *
 * `expiresAt` is computed from the project's TTL by the caller, because only the
 * caller can read project settings; this function just pairs value with time.
 */
export function toOnboardingVerificationStates(
  markers: OnboardingVerificationMarkers | undefined,
  expiry: { email?: string; phone?: string },
): OnboardingVerificationStates {
  const states: OnboardingVerificationStates = {};
  // A marker without a value is not usable: the server's gate is keyed by the
  // contact string, so "verified, but we forgot which address" cannot pass it.
  if (isTruthyMarker(markers?.isEmailVerified) && markers?.verifiedEmail) {
    states.email = { value: String(markers.verifiedEmail), expiresAt: expiry.email };
  }
  if (isTruthyMarker(markers?.isPhoneVerified) && markers?.verifiedPhone) {
    states.phone = { value: String(markers.verifiedPhone), expiresAt: expiry.phone };
  }
  return states;
}

/** Form state stores these as booleans or as the strings a form control produced. */
function isTruthyMarker(value: boolean | string | undefined): boolean {
  return value === true || value === 'true';
}

function toContactVerification(
  states: OnboardingVerificationStates,
  channel: AppVerificationChannel,
  now: number,
): AppContactVerification {
  const state = states[channel];
  return isVerificationLive(state, undefined, now) && state?.expiresAt
    ? { done: true, expiresAt: state.expiresAt }
    : { done: false };
}

/**
 * The `onboardingStatus` topic payload.
 *
 * `kyb` is null when the profile carries no organisation KYB status — an
 * individual customer, or a business application whose organisation has not
 * become the active one yet. Null is "not applicable"; 'not-started' would claim
 * a company exists and has not begun, which is a different thing.
 *
 * `submitted` is passed in rather than derived here because the caller knows both
 * halves of it: the platform's statuses and whether this host session performed a
 * submit (a provider can answer a submit with 'not-started').
 */
export function toWidgetOnboardingStatus(
  profile: ProfileStateModel | undefined,
  input: {
    verified: OnboardingVerificationStates;
    submitted: boolean;
    now?: number;
  },
): AppOnboardingStatus {
  const now = input.now ?? Date.now();
  const rawKyb = profile?.activeOrganisationKybStatus || profile?.kybVerificationStatus;
  return {
    kyc: normaliseOnboardingCheckStatus(profile?.kycVerificationStatus),
    kyb: rawKyb ? normaliseOnboardingCheckStatus(rawKyb) : null,
    profileComplete: String(profile?.profileCompletionStatus ?? '').toLowerCase() === 'completed',
    verified: {
      email: toContactVerification(input.verified, 'email', now),
      phone: toContactVerification(input.verified, 'phone', now),
    },
    canSubmit: !input.submitted && outstandingPrerequisites({ verified: input.verified, now }).length === 0,
  };
}

/**
 * The widget's individual payload as `POST /users/onboarding-individual` accepts
 * it.
 *
 * Three things are worth knowing about the translation:
 *
 * 1. **`extra` becomes `extraAttributes` verbatim.** The endpoint validates that
 *    bag server-side per provider; the bridge deliberately neither filters nor
 *    renames its keys, because guessing at a provider's vocabulary is exactly
 *    what the loose type exists to avoid. It is only attached when non-empty —
 *    an empty object is not the same as absent to every provider path.
 * 2. **The verification tokens are host-held and added here.** They are
 *    single-use secrets the server minted at OTP time and never crossed to the
 *    widget; onboarding has no authenticated user, so they are the only thing
 *    binding the verification to this client.
 * 3. **`emailVerified` / `phoneVerified` are advisory client claims**, not the
 *    gate — the server checks its own verification state either way. They are
 *    sent as the strings the platform sends, for parity with the native flow.
 */
export function toOnboardIndividualWireRequest(
  payload: OnboardIndividualWidgetRequest,
  tokens: { emailVerificationToken?: string; phoneVerificationToken?: string } = {},
): OnboardIndividualWireRequest {
  const address = payload.address;
  const request: OnboardIndividualWireRequest = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    dob: payload.dob,
    phoneNumber: payload.phone,
    country: address.country,
    address1: address.addressLine1,
    address2: address.addressLine2 ?? '',
    city: address.city,
    county: address.county ?? '',
    postalCode: address.postalCode,
    emailVerified: 'true',
    phoneVerified: 'true',
  };
  if (payload.middleName) request.middleName = payload.middleName;
  if (address.addressLine3) request.address3 = address.addressLine3;
  if (address.addressLine4) request.address4 = address.addressLine4;
  if (payload.password) request.password = payload.password;
  if (payload.currentAddressFromDate) request.currentAddressFromDate = payload.currentAddressFromDate;
  if (payload.previousAddresses?.length) {
    request.previousAddresses = payload.previousAddresses.map((previous) => ({
      country: previous.country,
      address1: previous.addressLine1,
      address2: previous.addressLine2 ?? '',
      address3: previous.addressLine3 ?? '',
      address4: previous.addressLine4 ?? '',
      city: previous.city,
      county: previous.county ?? '',
      postalCode: previous.postalCode,
      fromDate: previous.fromDate,
      toDate: previous.toDate,
    }));
  }
  if (payload.extra && Object.keys(payload.extra).length) {
    request.extraAttributes = { ...payload.extra };
  }
  if (tokens.emailVerificationToken) request.emailVerificationToken = tokens.emailVerificationToken;
  if (tokens.phoneVerificationToken) request.phoneVerificationToken = tokens.phoneVerificationToken;
  return request;
}

function toBusinessAddressWireRequest(address: AppBusinessAddress): BusinessOnboardingAddressRequest {
  const wire: BusinessOnboardingAddressRequest = {
    country: address.country,
    city: address.city,
    postalCode: address.postalCode,
  };
  if (address.addressLine1) wire.address1 = address.addressLine1;
  if (address.addressLine2) wire.address2 = address.addressLine2;
  if (address.addressLine3) wire.address3 = address.addressLine3;
  if (address.addressLine4) wire.address4 = address.addressLine4;
  if (address.state) wire.state = address.state;
  return wire;
}

function toBusinessMemberWireRequest(member: AppBusinessMember): BusinessOnboardingMemberRequest {
  const wire: BusinessOnboardingMemberRequest = {
    isPrimary: member.isPrimary === true,
    position: member.position,
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
    phone: member.phone,
    dob: member.dob,
  };
  if (member.title) wire.title = member.title;
  if (member.nationality) wire.nationality = member.nationality;
  if (member.taxResidency) wire.taxResidency = member.taxResidency;
  if (typeof member.ownerOfBusinessPercent === 'number') {
    wire.ownerOfBusinessPercent = member.ownerOfBusinessPercent;
  }
  if (typeof member.shareholderPercent === 'number') {
    wire.shareholderPercent = member.shareholderPercent;
  }
  if (member.address) wire.address = toBusinessAddressWireRequest(member.address);
  if (member.password) wire.password = member.password;
  if (member.securityQuestion?.length) wire.securityQuestion = member.securityQuestion;
  if (member.linkedAccount) wire.linkedAccount = member.linkedAccount;
  return wire;
}

/**
 * The widget's business payload as `POST /organisations/onboarding` accepts it.
 *
 * A close mapping, because that endpoint's DTO is already a structured
 * company+members model — the only translation is the address field names
 * (`addressLine1` -> `address1`) and dropping absent optionals rather than
 * sending them as `undefined` (the DTO's transforms run on whatever key is
 * present, and several of them would throw on `undefined`).
 *
 * There is still nowhere for provider-specific company fields to go: the
 * endpoint's extra-attributes bag is honoured only for `roleName` and the
 * service stores the organisation's own extras as an empty bag, which is why
 * OnboardBusinessRequest has no `extra`. Adding one here would be a field the
 * platform silently discards.
 *
 * The verification tokens are host-held and added here, exactly as in the
 * individual mapper above: the endpoint runs the same OTP gate on the primary
 * member's contacts, and the tokens are the only thing binding that
 * verification to this client.
 */
export function toBusinessOnboardingWireRequest(
  payload: OnboardBusinessRequest,
  tokens: { emailVerificationToken?: string; phoneVerificationToken?: string } = {},
): BusinessOnboardingRequest {
  const request: BusinessOnboardingRequest = {
    name: payload.name,
    identificationNumber: payload.identificationNumber,
    companyAddress: toBusinessAddressWireRequest(payload.companyAddress),
    members: payload.members.map(toBusinessMemberWireRequest),
  };
  if (payload.incorporationDate) request.incorporationDate = payload.incorporationDate;
  if (tokens.emailVerificationToken) request.emailVerificationToken = tokens.emailVerificationToken;
  if (tokens.phoneVerificationToken) request.phoneVerificationToken = tokens.phoneVerificationToken;
  return request;
}

/**
 * The business submit's result, minus the follow-up flag — same shape of
 * honesty as toOnboardIndividualResult above: whether a redirect happened is
 * the bridge's knowledge, not this mapper's. `status` is the KYB status the
 * endpoint answered with, normalised into the SDK's shared vocabulary.
 */
export function toOnboardBusinessResult(response: {
  id?: string;
  primaryUserId?: string;
  status?: string;
}): Omit<OnboardBusinessResult, 'redirected'> {
  return {
    organisationId: response?.id ?? '',
    primaryUserId: response?.primaryUserId ?? '',
    status: normaliseOnboardingCheckStatus(response?.status),
  };
}

/**
 * The individual submit's result, minus the follow-up flags.
 *
 * The onboarding response also carries auto-login access/refresh tokens; they are
 * host-only and deliberately absent here, exactly like the KYC access token in
 * savings.create. What a widget learns about them is the `signedIn` boolean the
 * *bridge* adds after performing the follow-up it asked for — this mapper cannot
 * know whether that happened, so it does not pretend to (hence the Omit rather
 * than a hardcoded `signedIn: false`).
 */
export function toOnboardIndividualResult(response: {
  id?: string;
  status?: string;
}): Omit<OnboardIndividualResult, 'signedIn' | 'redirected'> {
  const result: Omit<OnboardIndividualResult, 'signedIn' | 'redirected'> = {
    status: normaliseOnboardingCheckStatus(response?.status),
  };
  if (response?.id) result.userId = response.id;
  return result;
}

/**
 * A stored document row as the contract sees it. `contentType` stays unset: the platform's
 * document rows record a file name and a storage id, never a MIME type.
 */
export function toWidgetStoredDocument(document: FileDocumentResponse): AppStoredDocument {
  const mapped: AppStoredDocument = {
    documentId: document.id,
    name: document.name,
  };
  if (document.category) mapped.category = document.category as AppStoredDocument['category'];
  if (document.createdAt) mapped.createdAt = new Date(document.createdAt).toISOString();
  return mapped;
}
